import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import * as agentService from '../services/agentService';

const AgentContext = createContext(null);

export const useAgent = () => {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error('useAgent must be used within AgentProvider');
  return ctx;
};

export const AgentProvider = ({ children }) => {
  const { authenticatedFetch } = useAuth();

  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Session data
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);

  // Conversation state
  const [messages, setMessages] = useState([]); // UI message objects
  const [traceSteps, setTraceSteps] = useState([]); // current trace block steps
  const [pendingPlan, setPendingPlan] = useState(null);
  const [entityPreview, setEntityPreview] = useState(null); // {type, data}

  // Streaming state
  const [streamingState, setStreamingState] = useState('idle');
  // idle | thinking | awaiting_confirmation | executing | error

  // Task breakdown (hierarchical plan from decompose_tasks)
  const [taskBreakdown, setTaskBreakdown] = useState(null); // array | null
  const [taskStatuses, setTaskStatuses] = useState({}); // map of id → 'pending'|'in_progress'|'completed'|'failed'
  // Sequential index ref: tracks which flat task/subtask is currently active
  const taskStepIndexRef = useRef(0);
  // Flattened list of task ids (tasks then subtasks in document order)
  const flatTaskIdsRef = useRef([]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Abort ref for cancelling active SSE stream
  const abortStreamRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Task breakdown helpers
  // ---------------------------------------------------------------------------

  const resetTaskBreakdown = useCallback(() => {
    setTaskBreakdown(null);
    setTaskStatuses({});
    taskStepIndexRef.current = 0;
    flatTaskIdsRef.current = [];
  }, []);

  // Advance the sequential task index: mark current id completed, next in_progress
  const advanceTaskStep = useCallback(() => {
    const flat = flatTaskIdsRef.current;
    const idx = taskStepIndexRef.current;
    if (!flat.length) return;

    setTaskStatuses((prev) => {
      const next = { ...prev };
      if (flat[idx]) next[flat[idx]] = 'completed';
      if (flat[idx + 1]) next[flat[idx + 1]] = 'in_progress';
      return next;
    });
    taskStepIndexRef.current = idx + 1;
  }, []);

  // Mark the current active task id in the flat list as the given status
  const setCurrentTaskStatus = useCallback((status) => {
    const flat = flatTaskIdsRef.current;
    const idx = taskStepIndexRef.current;
    if (!flat.length || !flat[idx]) return;
    setTaskStatuses((prev) => ({ ...prev, [flat[idx]]: status }));
  }, []);

  // Mark all remaining tasks completed (session done)
  const completeAllTasks = useCallback(() => {
    const flat = flatTaskIdsRef.current;
    if (!flat.length) return;
    setTaskStatuses((prev) => {
      const next = { ...prev };
      flat.forEach((id) => {
        if (next[id] !== 'completed') next[id] = 'completed';
      });
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Panel controls
  // ---------------------------------------------------------------------------

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const toggleFullScreen = useCallback(() => setIsFullScreen((v) => !v), []);

  // ---------------------------------------------------------------------------
  // Session list
  // ---------------------------------------------------------------------------

  const loadSessions = useCallback(async () => {
    try {
      const list = await agentService.getSessions(authenticatedFetch);
      setSessions(list);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  }, [authenticatedFetch]);

  const loadSession = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const session = await agentService.getSession(authenticatedFetch, sessionId);
      setActiveSession(session);
      setTraceSteps([]);
      setError(null);

      // Prefer ui_messages (persisted UI state with plan/error/complete cards).
      // Fall back to reconstructing plain-text messages for older sessions.
      let uiMessages;
      if (session.ui_messages && session.ui_messages.length > 0) {
        uiMessages = session.ui_messages.map((m, i) => ({
          ...m,
          id: m.id || `loaded-${i}`,
        }));
      } else {
        uiMessages = (session.messages || [])
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m, i) => ({
            id: `msg-${i}`,
            role: m.role === 'assistant' ? 'agent' : 'user',
            content: Array.isArray(m.content)
              ? m.content.filter((b) => b.type === 'text').map((b) => b.text).join('')
              : m.content,
            type: 'text',
          }))
          .filter((m) => m.content);
      }

      setMessages(uiMessages);
      setStreamingState(session.status === 'awaiting_confirmation' ? 'awaiting_confirmation' : 'idle');
      if (session.pending_plan) {
        setPendingPlan(session.pending_plan);
      } else {
        setPendingPlan(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  // ---------------------------------------------------------------------------
  // Start new session
  // ---------------------------------------------------------------------------

  const createSession = useCallback(async (pageContext) => {
    try {
      const { session_id } = await agentService.createSession(authenticatedFetch, pageContext);
      setActiveSession({ id: session_id, status: 'in_progress', messages: [], tool_calls: [] });
      setMessages([]);
      setTraceSteps([]);
      setPendingPlan(null);
      setEntityPreview(null);
      setError(null);
      setStreamingState('idle');
      resetTaskBreakdown();
      return session_id;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [authenticatedFetch, resetTaskBreakdown]);

  // ---------------------------------------------------------------------------
  // Kill / X session
  // ---------------------------------------------------------------------------

  const killSession = useCallback(async () => {
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    if (activeSession?.id) {
      try {
        await agentService.killSession(authenticatedFetch, activeSession.id);
      } catch (err) {
        console.error('Kill session error:', err);
      }
    }
    setActiveSession(null);
    setMessages([]);
    setTraceSteps([]);
    setPendingPlan(null);
    setEntityPreview(null);
    setStreamingState('idle');
    setError(null);
    resetTaskBreakdown();
    await loadSessions();
  }, [authenticatedFetch, activeSession, loadSessions, resetTaskBreakdown]);

  // ---------------------------------------------------------------------------
  // Delete a session from history (does not have to be the active session)
  // ---------------------------------------------------------------------------

  const deleteSession = useCallback(async (sessionId) => {
    try {
      await agentService.deleteSession(authenticatedFetch, sessionId);
      // If the deleted session is the active one, clear it
      if (activeSession?.id === sessionId) {
        if (abortStreamRef.current) {
          abortStreamRef.current();
          abortStreamRef.current = null;
        }
        setActiveSession(null);
        setMessages([]);
        setTraceSteps([]);
        setPendingPlan(null);
        setEntityPreview(null);
        setStreamingState('idle');
        setError(null);
        resetTaskBreakdown();
      }
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      console.error('Delete session error:', err);
    }
  }, [authenticatedFetch, activeSession, resetTaskBreakdown]);

  // ---------------------------------------------------------------------------
  // Cancel pending plan (keep session alive, mark plan as cancelled in UI)
  // ---------------------------------------------------------------------------

  const cancelPlan = useCallback(async () => {
    if (!activeSession?.id) return;
    try {
      await agentService.cancelPlan(authenticatedFetch, activeSession.id);
    } catch (err) {
      console.error('Cancel plan error:', err);
    }
    // Mark the most recent plan message as cancelled so the card shows a cancelled state
    setMessages((prev) => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].type === 'plan') {
          updated[i] = { ...updated[i], cancelled: true };
          break;
        }
      }
      return updated;
    });
    setPendingPlan(null);
    setStreamingState('idle');
  }, [authenticatedFetch, activeSession]);

  // ---------------------------------------------------------------------------
  // SSE event handler
  // ---------------------------------------------------------------------------

  const handleSSEEvent = useCallback((event) => {
    switch (event.type) {
      case 'task_breakdown': {
        const tasks = event.tasks || [];
        // Build flat list of ids: task, then its subtasks, in order
        const flat = [];
        tasks.forEach((t) => {
          flat.push(t.id);
          (t.subtasks || []).forEach((s) => flat.push(s.id));
        });
        flatTaskIdsRef.current = flat;
        taskStepIndexRef.current = 0;
        // Init all pending, first item in_progress
        const initialStatuses = {};
        flat.forEach((id, i) => { initialStatuses[id] = i === 0 ? 'in_progress' : 'pending'; });
        setTaskStatuses(initialStatuses);
        setTaskBreakdown(tasks);
        break;
      }

      case 'trace':
        // Advance sequential task status tracking
        advanceTaskStep();
        setTraceSteps((prev) => [
          ...prev,
          { label: event.label, status: 'done' },
        ]);
        break;

      case 'plan':
        setPendingPlan({ steps: event.steps, session_title: event.session_title });
        setStreamingState('awaiting_confirmation');
        // Add plan to messages as a special message type
        setMessages((prev) => [
          ...prev,
          {
            id: `plan-${Date.now()}`,
            role: 'agent',
            type: 'plan',
            plan: { steps: event.steps, session_title: event.session_title },
          },
        ]);
        break;

      case 'message':
        setMessages((prev) => [
          ...prev,
          { id: `msg-${Date.now()}`, role: 'agent', type: 'text', content: event.content },
        ]);
        setStreamingState('idle');
        break;

      case 'session_title':
        setActiveSession((prev) => prev ? { ...prev, title: event.title } : prev);
        break;

      case 'tool_executing':
        setEntityPreview({ type: event.entity_type, data: event.entity_data, tool: event.tool });
        setCurrentTaskStatus('in_progress');
        setTraceSteps((prev) => [
          ...prev,
          { label: `Executing: ${event.tool.replace(/_/g, ' ')}`, status: 'in_progress' },
        ]);
        break;

      case 'tool_complete':
        advanceTaskStep();
        setTraceSteps((prev) => {
          const updated = [...prev];
          // Find last in_progress step and mark done
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].status === 'in_progress') {
              updated[i] = { ...updated[i], status: 'done', summary: event.result_summary };
              break;
            }
          }
          return updated;
        });
        break;

      case 'tool_error':
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'agent',
            type: 'error',
            error: event.error,
            tool: event.tool,
            completed_steps: event.completed_steps,
            options: event.options,
          },
        ]);
        setStreamingState('error');
        break;

      case 'session_complete':
        completeAllTasks();
        setActiveSession((prev) => prev ? { ...prev, status: 'complete' } : prev);
        setMessages((prev) => {
          // Mark any unresolved plan cards as executed so their buttons freeze
          const withExecuted = prev.map((m) =>
            m.type === 'plan' && !m.cancelled ? { ...m, executed: true } : m
          );
          return [
            ...withExecuted,
            {
              id: `complete-${Date.now()}`,
              role: 'agent',
              type: 'session_complete',
              changes: event.changes,
              affected_resources: event.affected_resources,
              report_urls: event.report_urls || [],
            },
          ];
        });
        setStreamingState('idle');
        loadSessions(); // refresh session list
        break;

      case 'error':
        setError(event.detail);
        setStreamingState('error');
        break;

      default:
        break;
    }
  }, [loadSessions, advanceTaskStep, setCurrentTaskStatus, completeAllTasks]);

  // ---------------------------------------------------------------------------
  // Send message
  // ---------------------------------------------------------------------------

  const sendMessage = useCallback(async (content, mentions = [], attachments = [], pageContext = null) => {
    if (!content.trim()) return;

    let sessionId = activeSession?.id;

    // Auto-create session if none
    if (!sessionId) {
      sessionId = await createSession(pageContext);
      if (!sessionId) return;
    }

    // Add user message to UI immediately
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', type: 'text', content },
    ]);
    setTraceSteps([]);
    setStreamingState('thinking');
    setError(null);
    resetTaskBreakdown();

    const payload = {
      content,
      mentions,
      attachments,
      page_context: pageContext,
    };

    // Cancel any active stream
    if (abortStreamRef.current) {
      abortStreamRef.current();
    }

    abortStreamRef.current = agentService.streamMessage(
      authenticatedFetch,
      sessionId,
      payload,
      handleSSEEvent,
      (err) => {
        setError(err.message);
        setStreamingState('error');
      },
      () => {
        abortStreamRef.current = null;
      }
    );
  }, [authenticatedFetch, activeSession, createSession, handleSSEEvent, resetTaskBreakdown]);

  // ---------------------------------------------------------------------------
  // Confirm plan
  // ---------------------------------------------------------------------------

  const confirmPlan = useCallback(async (selectedStepIndices) => {
    if (!activeSession?.id) return;

    setStreamingState('executing');
    setTraceSteps([]);

    if (abortStreamRef.current) {
      abortStreamRef.current();
    }

    abortStreamRef.current = agentService.confirmPlan(
      authenticatedFetch,
      activeSession.id,
      selectedStepIndices,
      handleSSEEvent,
      (err) => {
        setError(err.message);
        setStreamingState('error');
      },
      () => {
        abortStreamRef.current = null;
      }
    );
  }, [authenticatedFetch, activeSession, handleSSEEvent]);

  // ---------------------------------------------------------------------------
  // Retry / skip a failed step
  // ---------------------------------------------------------------------------

  const retryStep = useCallback((completedSteps) => {
    if (!activeSession?.id || !pendingPlan) return;
    const totalSteps = pendingPlan.steps.length;
    const failedIndex = (completedSteps || []).length;
    // Re-run the failed step plus all subsequent steps
    const stepsToRun = Array.from({ length: totalSteps - failedIndex }, (_, i) => failedIndex + i);
    if (stepsToRun.length === 0) return;
    setStreamingState('executing');
    setTraceSteps([]);
    if (abortStreamRef.current) abortStreamRef.current();
    abortStreamRef.current = agentService.confirmPlan(
      authenticatedFetch,
      activeSession.id,
      stepsToRun,
      handleSSEEvent,
      (err) => { setError(err.message); setStreamingState('error'); },
      () => { abortStreamRef.current = null; }
    );
  }, [authenticatedFetch, activeSession, pendingPlan, handleSSEEvent]);

  const skipStep = useCallback((completedSteps) => {
    if (!activeSession?.id || !pendingPlan) return;
    const totalSteps = pendingPlan.steps.length;
    const failedIndex = (completedSteps || []).length;
    // Skip the failed step; run all steps after it
    const stepsToRun = Array.from({ length: totalSteps - failedIndex - 1 }, (_, i) => failedIndex + 1 + i);
    if (stepsToRun.length === 0) {
      // Nothing left to run after skipping — transition back to idle
      setStreamingState('idle');
      return;
    }
    setStreamingState('executing');
    setTraceSteps([]);
    if (abortStreamRef.current) abortStreamRef.current();
    abortStreamRef.current = agentService.confirmPlan(
      authenticatedFetch,
      activeSession.id,
      stepsToRun,
      handleSSEEvent,
      (err) => { setError(err.message); setStreamingState('error'); },
      () => { abortStreamRef.current = null; }
    );
  }, [authenticatedFetch, activeSession, pendingPlan, handleSSEEvent]);

  // ---------------------------------------------------------------------------
  // Undo session
  // ---------------------------------------------------------------------------

  const undoSession = useCallback(async () => {
    if (!activeSession?.id) return;
    try {
      await agentService.undoSession(authenticatedFetch, activeSession.id);
      await loadSessions();
      setActiveSession(null);
      setMessages([]);
    } catch (err) {
      setError(err.message);
    }
  }, [authenticatedFetch, activeSession, loadSessions]);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const value = {
    // Panel state
    isOpen,
    isFullScreen,
    openPanel,
    closePanel,
    toggleFullScreen,

    // Sessions
    sessions,
    activeSession,
    loadSessions,
    loadSession,
    createSession,
    killSession,
    deleteSession,

    // Conversation
    messages,
    traceSteps,
    pendingPlan,
    entityPreview,
    streamingState,
    error,
    loading,

    // Task breakdown (hierarchical thinking view)
    taskBreakdown,
    taskStatuses,

    // Actions
    sendMessage,
    confirmPlan,
    cancelPlan,
    retryStep,
    skipStep,
    undoSession,
  };

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};
